import React, { useState, useRef, useEffect } from "react"
import { useTranslation } from "react-i18next"
import { navigate } from "gatsby"
import { languages, defaultLanguage, languageCodes } from "../i18n/config"
import { setCookie } from "@filmustage/shared"

const flags = {
  GB: "\uD83C\uDDEC\uD83C\uDDE7",
  ES: "\uD83C\uDDEA\uD83C\uDDF8",
  FR: "\uD83C\uDDEB\uD83C\uDDF7",
  DE: "\uD83C\uDDE9\uD83C\uDDEA",
  IT: "\uD83C\uDDEE\uD83C\uDDF9",
  PT: "\uD83C\uDDF5\uD83C\uDDF9",
  BR: "\uD83C\uDDE7\uD83C\uDDF7",
  UA: "\uD83C\uDDFA\uD83C\uDDE6",
  MT: "\uD83C\uDDF2\uD83C\uDDF9",
  TH: "\uD83C\uDDF9\uD83C\uDDED",
}

// White-red-white (BChB) flag as inline SVG, sized to match emoji flags (~1.2em)
function BChBFlag() {
  return (
    <svg width="1em" height="1em" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
      <rect x="1" y="3" width="18" height="14" rx="2" fill="#FFFFFF" />
      <rect x="1" y="3" width="18" height="4.67" fill="#FFFFFF" />
      <rect x="1" y="7.67" width="18" height="4.67" fill="#CE1720" />
      <rect x="1" y="12.33" width="18" height="4.67" fill="#FFFFFF" />
      <rect x="1" y="3" width="18" height="14" rx="2" fill="none" stroke="#555" strokeWidth="0.5" />
    </svg>
  )
}

function FlagIcon({ flagCode }) {
  if (flagCode === "BChB") {
    return <BChBFlag />
  }
  return <span>{flags[flagCode]}</span>
}

function getPathWithoutLang(pathname) {
  const parts = pathname.split("/").filter(Boolean)
  if (parts.length > 0 && languageCodes.includes(parts[0])) {
    const rest = parts.slice(1).join("/")
    return rest ? `/${rest}/` : "/"
  }
  // Ensure trailing slash for consistency with Gatsby's trailingSlash: 'always'
  if (pathname !== "/" && !pathname.endsWith("/")) {
    return pathname + "/"
  }
  return pathname
}

function LanguageSwitcher() {
  const { i18n } = useTranslation()
  const [open, setOpen] = useState(false)
  const ref = useRef(null)

  const currentLang = languages.find(l => l.code === i18n.language) || languages[0]

  useEffect(() => {
    function handleClickOutside(e) {
      if (ref.current && !ref.current.contains(e.target)) {
        setOpen(false)
      }
    }
    document.addEventListener("mousedown", handleClickOutside)
    return () => document.removeEventListener("mousedown", handleClickOutside)
  }, [])

  function switchLanguage(langCode) {
    setOpen(false)
    i18n.changeLanguage(langCode)
    setCookie("i18nextLng", langCode)

    const pathname = window.location.pathname

    // Blog and legal pages are EN-only - i18n updates UI strings, no navigation needed
    const legalPages = ["/terms/", "/privacy/", "/content-policy/", "/cookie-policy/", "/referral-program-agreement/"]
    if (pathname.startsWith("/blog") || legalPages.includes(pathname)) {
      return
    }

    const pathWithoutLang = getPathWithoutLang(pathname)

    // Client-side navigation to the correct lang URL (no full page reload)
    if (langCode === defaultLanguage) {
      navigate(pathWithoutLang || "/")
    } else {
      navigate(`/${langCode}${pathWithoutLang}`)
    }
  }

  return (
    <div className="relative" ref={ref}>
      <button
        className="flex items-center gap-1.5 text-gray-300 hover:text-gray-200 px-2 py-2 transition duration-150 ease-in-out text-sm"
        onClick={() => setOpen(!open)}
        aria-label="Select language"
      >
        <FlagIcon flagCode={currentLang.flag} />
        <span className="hidden lg:inline">{currentLang.code.toUpperCase()}</span>
        <svg
          className={`w-3 h-3 fill-current text-gray-500 transition-transform ${open ? "rotate-180" : ""}`}
          viewBox="0 0 12 12"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path d="M10.28 4.305L6.0 8.575 1.72 4.305a1 1 0 00-1.44 1.39l5 5a1 1 0 001.44 0l5-5a1 1 0 00-1.44-1.39z" />
        </svg>
      </button>

      {open && (
        <div className="absolute right-0 top-full mt-1 bg-gray-800 border border-gray-700 rounded-md shadow-lg py-1 min-w-[160px] z-50">
          {languages.map(lang => (
            <button
              key={lang.code}
              className={`w-full text-left px-3 py-1.5 text-sm flex items-center gap-2 hover:bg-gray-700 transition-colors ${
                lang.code === currentLang.code
                  ? "text-fOrange-900"
                  : "text-gray-300"
              }`}
              onClick={() => switchLanguage(lang.code)}
            >
              <FlagIcon flagCode={lang.flag} />
              <span>{lang.name}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  )
}

export default LanguageSwitcher
